import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Button, Modal, Typography } from 'antd';
import { isHubAlarmRinging } from '../hubAlerts/hubAlarm';
import {
  FORCED_UPDATES_ENABLED,
  LONG_AWAY_MS,
  RELEASE_POLL_MS,
  RELOAD_GUARD_KEY,
  UPDATE_COUNTDOWN_SECONDS,
  canForceReload,
  needsUpdate,
  nextReloadAttempt,
  parseReloadAttempt,
  refreshesImmediately,
} from './releaseRules';

declare const __SENTRY_RELEASE__: string | null;

/**
 * The build this page runs: `warehouse@<sha>`, baked in at build time from the workflow's
 * SENTRY_RELEASE, the same value the deploy writes to release-info.json. Absent in local builds,
 * which turns the watcher off. Replaceable in tests.
 */
export const runningRelease = {
  value:
    typeof __SENTRY_RELEASE__ === 'string' && __SENTRY_RELEASE__.trim() !== ''
      ? __SENTRY_RELEASE__
      : undefined,
};

/** The page refresh (replaced in tests, where jsdom cannot reload). */
export const pageReload = { run: () => window.location.reload() };

/** While a ringing new-order alarm holds a refresh back, look again this often. */
const ALARM_RECHECK_MS = 1000;
/** A refresh that did not happen (the browser refused it) frees Update now again after this long. */
const RELOAD_SETTLE_MS = 10_000;

/** Separate from the update guard, so the two never reset each other's attempt count. */
const PRELOAD_GUARD_KEY = `${RELOAD_GUARD_KEY}-preload`;

const lastAttempt = (guardKey = RELOAD_GUARD_KEY) => {
  try {
    return parseReloadAttempt(sessionStorage.getItem(guardKey));
  } catch {
    return null;
  }
};

const fetchLatestRelease = async (): Promise<string | null> => {
  try {
    const response = await fetch(`/release-info.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const info = (await response.json()) as { release?: unknown };
    return typeof info.release === 'string' ? info.release : null;
  } catch {
    return null;
  }
};

/** Fields a person types into (radio buttons and checkboxes always carry a value). */
const TYPED_FIELDS =
  'textarea, input:not([type]), input[type="text"], input[type="email"], input[type="tel"], ' +
  'input[type="password"], input[type="number"], input[type="search"], input[type="url"]';

/** Something typed on a sign-in page (an operator mid-login) is not thrown away by a refresh. */
const hasTypedInput = () =>
  Array.from(document.querySelectorAll<HTMLInputElement>(TYPED_FIELDS)).some(
    field => field.value.trim() !== '',
  );

/** Refreshes into the new build, within the attempt limits of `canForceReload`. */
const reloadInto = async (release: string, force = false, guardKey = RELOAD_GUARD_KEY) => {
  const last = lastAttempt(guardKey);
  if (!canForceReload(release, last, Date.now(), force)) return false;
  try {
    sessionStorage.setItem(guardKey, JSON.stringify(nextReloadAttempt(release, last, Date.now())));
  } catch {
    // Without storage the guard is off; the poll interval still spaces attempts.
  }
  // Pick up a new service worker too (it activates at once); never wait long for it.
  await Promise.race([
    navigator.serviceWorker
      ?.getRegistration('/')
      .then(registration => registration?.update())
      .catch(() => undefined),
    new Promise(resolve => setTimeout(resolve, 2000)),
  ]);
  pageReload.run();
  return true;
};

/**
 * Keeps every open Hub on the latest build within about two minutes of a deploy. While Hub is
 * visible it checks `/release-info.json` every minute and whenever it comes back to the front. A
 * newer build refreshes at once on a sign-in page with nothing typed, or when Hub returns after 5+
 * minutes away; otherwise a modal counts down 60 seconds (Update now refreshes earlier) and Hub also
 * refreshes if it goes to the background meanwhile. Only Update now refreshes over a ringing
 * new-order alarm; every other path waits until the alarm is handled.
 */
const ReleaseWatcher = () => {
  const { pathname } = useLocation();
  const [latest, setLatest] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(UPDATE_COUNTDOWN_SECONDS);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const latestRef = useRef(latest);
  latestRef.current = latest;

  const applying = useRef(false);
  const alarmWait = useRef<number | undefined>(undefined);

  /** `force`: the operator pressed Update now. */
  const apply = useCallback((release: string, force = false) => {
    const attempt = async () => {
      window.clearTimeout(alarmWait.current);
      if (applying.current) return;
      if (!force && isHubAlarmRinging()) {
        // A refresh would silence the alarm and drop its card: try again once it is handled.
        alarmWait.current = window.setTimeout(() => void attempt(), ALARM_RECHECK_MS);
        return;
      }
      applying.current = true;
      if (!(await reloadInto(release, force))) {
        applying.current = false;
        setLatest(null);
        return;
      }
      // Still here: the browser did not refresh. Free Update now and count down again.
      window.setTimeout(() => {
        applying.current = false;
        setSecondsLeft(current => (current <= 0 ? UPDATE_COUNTDOWN_SECONDS : current));
      }, RELOAD_SETTLE_MS);
    };
    void attempt();
  }, []);

  /** `returned`: Hub came back after a long break, so nobody is in the middle of a task. */
  const check = useCallback(
    async (returned = false) => {
      if (!runningRelease.value || document.visibilityState !== 'visible') return;
      const release = await fetchLatestRelease();
      if (!release || !needsUpdate(runningRelease.value, release)) return;
      // Already refreshed for this build moments ago and still on the old one: wait for the guard.
      if (!canForceReload(release, lastAttempt(), Date.now())) return;
      if (returned || (refreshesImmediately(false, pathRef.current) && !hasTypedInput())) {
        apply(release);
        return;
      }
      if (latestRef.current !== release) {
        setSecondsLeft(UPDATE_COUNTDOWN_SECONDS);
        setLatest(release);
      }
    },
    [apply],
  );

  useEffect(() => {
    if (!FORCED_UPDATES_ENABLED || !runningRelease.value) return undefined;
    void check();
    const timer = window.setInterval(() => void check(), RELEASE_POLL_MS);
    let hiddenAt: number | null = null;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void check(hiddenAt !== null && Date.now() - hiddenAt >= LONG_AWAY_MS);
        hiddenAt = null;
        return;
      }
      hiddenAt = Date.now();
      if (latestRef.current) {
        // The modal was up and Hub went to the background: refresh now rather than mid-task later.
        apply(latestRef.current);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    // A lazily loaded page file that fails to load usually means a newer build: refresh once,
    // unless a new-order alarm rings (the page error screen shows; the alarm and its card stay).
    const onPreloadError = (event: Event) => {
      if (isHubAlarmRinging()) return;
      const key = `preload:${runningRelease.value}`;
      if (canForceReload(key, lastAttempt(PRELOAD_GUARD_KEY), Date.now())) {
        event.preventDefault();
        void reloadInto(key, false, PRELOAD_GUARD_KEY);
      }
    };
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(alarmWait.current);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, [apply, check]);

  // Countdown, then refresh (which waits while a new-order alarm rings).
  useEffect(() => {
    if (!latest) return undefined;
    const timer = window.setInterval(
      () => setSecondsLeft(current => Math.max(0, current - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [latest]);
  useEffect(() => {
    if (latest && secondsLeft <= 0) apply(latest);
  }, [apply, latest, secondsLeft]);

  if (!latest) return null;
  return (
    <Modal
      open
      title="Hub has been updated"
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        <Button type="primary" size="large" block onClick={() => apply(latest, true)}>
          Update now
        </Button>
      }
    >
      <Typography.Paragraph style={{ fontSize: 15 }}>
        A new version of Hub is ready.{' '}
        {secondsLeft > 0
          ? `Hub refreshes in ${secondsLeft} seconds.`
          : 'Hub refreshes as soon as the new order alert is handled.'}
      </Typography.Paragraph>
      <Typography.Text type="secondary">
        Finish what you are doing. Anything not yet saved has to be entered again after the refresh.
      </Typography.Text>
    </Modal>
  );
};

export default ReleaseWatcher;
